import { ICommand } from './ICommand';
import { getNodeMutationManager } from './mutations/NodeMutationManager';
import type { TableCellKey } from '../../stores/tableCellStore';
import { SessionMetrics, TABLE_SESSION_METRICS } from '../../domains/analytics';

interface UpdateTableCellTextCommandParams {
  nodeId: string;
  cellKey: TableCellKey;
  newText: string;
  oldText?: string;
}

export class UpdateTableCellTextCommand implements ICommand {
  id = `update-table-cell-text-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  name = 'Update Table Cell Text';
  timestamp = Date.now();

  constructor(private params: UpdateTableCellTextCommandParams) {}

  async execute(): Promise<void> {
    const mutationManager = getNodeMutationManager();
    const node = mutationManager.getNode(this.params.nodeId);
    if (!node) return;
    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const prevMap = (attrs.cellText as Record<string, string> | undefined) ?? {};
    const nextMap: Record<string, string> = { ...prevMap };
    const normalized = this.params.newText.trim();
    if (normalized.length === 0) {
      delete nextMap[this.params.cellKey];
    } else {
      nextMap[this.params.cellKey] = this.params.newText;
    }

    const changes = { attrs: { cellText: nextMap } } as const;
    const record = mutationManager.applyNodeChange(this.params.nodeId, changes);
    if (record) {
      SessionMetrics.increment(TABLE_SESSION_METRICS.CELLS_EDITED);
    }
  }

  async undo(): Promise<void> {
    const mutationManager = getNodeMutationManager();
    const node = mutationManager.getNode(this.params.nodeId);
    if (!node) return;
    const attrs = (node.attrs ?? {}) as Record<string, unknown>;
    const prevMap = (attrs.cellText as Record<string, string> | undefined) ?? {};
    const nextMap: Record<string, string> = { ...prevMap };

    const oldValue = this.params.oldText;
    if (typeof oldValue === 'string') {
      nextMap[this.params.cellKey] = oldValue;
    } else {
      delete nextMap[this.params.cellKey];
    }

    const changes = { attrs: { cellText: nextMap } } as const;
    mutationManager.applyNodeChange(this.params.nodeId, changes);
  }

  async redo(): Promise<void> {
    await this.execute();
  }

  canMergeWith(other: ICommand): boolean {
    if (!(other instanceof UpdateTableCellTextCommand)) return false;
    return (
      this.params.nodeId === other.params.nodeId &&
      this.params.cellKey === other.params.cellKey &&
      Math.abs(this.timestamp - other.timestamp) < 1000
    );
  }

  mergeWith(other: ICommand): ICommand {
    if (!(other instanceof UpdateTableCellTextCommand)) return this;
    return new UpdateTableCellTextCommand({
      nodeId: this.params.nodeId,
      cellKey: this.params.cellKey,
      newText: other.params.newText,
      oldText: this.params.oldText,
    });
  }

  getMetadata(): Record<string, unknown> {
    return {
      nodeId: this.params.nodeId,
      cellKey: this.params.cellKey,
    };
  }
}


